import React from "react";
import parse from 'html-react-parser';
import { SearchOutlined } from '@ant-design/icons';
import { Button, Col, Input, Layout, Popover, Row, Space, Table, Typography} from 'antd';
import type { InputRef } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FilterConfirmProps } from 'antd/es/table/interface';

import { BitmakerAPIClient } from "../../services/BitmakerApp";
import { SalesIssue } from "../../bitmaker/schemas/SalesIssue";
import { SalesSummary, SalesPipelineSummary, SalesData, SalesDataFromJSON } from "../../bitmaker/schemas/Sales";
import { fromDateToString, formatDate, isDateGreaterThan, compareDates } from "../../utils";
import { REDMINE_URL } from "../../constants";

const { Content } = Layout;
const { Title } = Typography;


interface Props {
}

interface State {
    salesData?: SalesData;
    ownerSearchText: string;
    ownerInputValue: string;
}

export class SalesDashboard extends React.PureComponent<Props, State> {
    searchInput = React.createRef<InputRef>();

    setSearchText = (ownerSearchText: string) => {
        this.setState({ ownerSearchText });
    };

    setInputValue = (ownerInputValue: string) => {
        this.setState({ ownerInputValue });
    };

    handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
    ) => {
        confirm();
        this.setSearchText(selectedKeys[0]);
    };
    
    handleReset = (clearFilters: () => void) => {
        clearFilters();
        this.setSearchText('');
        this.setInputValue('');
    };

    pipelineSummaryColumns: ColumnsType<SalesPipelineSummary> = [
        {
            dataIndex: 'pipeline_summary_name',
            key: 'pipeline_summary_name',
            align: 'center',
            render: (text) => (
                <p className="font-semibold">{text}</p>
            ),
        },
        {
            dataIndex: 'amount',
            key: 'amount',
            align: 'center',
            render: (text, record) => (
                record.pipeline_summary_name === "Total Amount Forecast" ? (
                    <p className="text-blue-700 font-bold text-lg">
                        ${text.toLocaleString()}
                    </p>
                ) : (
                    <p className="text-green-700 font-bold text-lg">
                        ${text.toLocaleString()}
                    </p>
                )
            ),
        },
    ];

    summaryColumns: ColumnsType<SalesSummary> = [
        {
            dataIndex: 'summary_name',
            key: 'summary_name',
            align: 'center',
            fixed: 'left',
            render: (text) => (
                <p className="font-semibold">{text}</p>
            ),
        },
        {
            title: 'Qualification',
            dataIndex: 'qualification',
            key: 'qualification',
            align: 'center',
            render: (text, record) => (
                record.summary_name === "Revenue" ? (
                    <p>${text.toLocaleString()}</p>
                ) : (
                    <p>{text}</p>
                )
            ),
        },
        {
            title: 'Analysis',
            dataIndex: 'analysis',
            key: 'analysis',
            align: 'center',
            render: (text, record) => (
                record.summary_name === "Revenue" ? (
                    <p>${text.toLocaleString()}</p>
                ) : (
                    <p>{text}</p>
                )
            ),
        },
        {
            title: 'Estimation',
            dataIndex: 'estimation',
            key: 'estimation',
            align: 'center',
            render: (text, record) => (
                record.summary_name === "Revenue" ? (
                    <p>${text.toLocaleString()}</p>
                ) : (
                    <p>{text}</p>
                )
            ),
        },
        {
            title: 'Proposal',
            dataIndex: 'proposal',
            key: 'proposal',
            align: 'center',
            render: (text, record) => (
                record.summary_name === "Revenue" ? (
                    <p>${text.toLocaleString()}</p>
                ) : (
                    <p>{text}</p>
                )
            ),
        },
        {
            title: 'Negotiation',
            dataIndex: 'negotiation',
            key: 'negotiation',
            align: 'center',
            render: (text, record) => (
                record.summary_name === "Revenue" ? (
                    <p>${text.toLocaleString()}</p>
                ) : (
                    <p>{text}</p>
                )
            ),
        },
        {
            title: 'Won',
            dataIndex: 'won',
            key: 'won',
            align: 'center',
            render: (text, record) => (
                record.summary_name === "Revenue" ? (
                    <p>${text.toLocaleString()}</p>
                ) : (
                    <p>{text}</p>
                )
            ),
        },
        {
            title: 'Lost',
            dataIndex: 'lost',
            key: 'lost',
            align: 'center',
            render: (text, record) => (
                record.summary_name === "Revenue" ? (
                    <p>${text.toLocaleString()}</p>
                ) : (
                    <p>{text}</p>
                )
            ),
        },
    ];

    issuesColumns: ColumnsType<SalesIssue> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: '5%',
            align: 'center',
            render: (_, record) => (
                <a href={`${REDMINE_URL}/issues/${record.id}`} target="_blank" rel="noreferrer">
                    {record.id}
                </a>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: '16%',
            render: (_, record) => (
                <a href={`${REDMINE_URL}/issues/${record.id}`} target="_blank" rel="noreferrer">
                    {record.subject}
                </a>
            ),
        },
        {
            title: 'Opportunity Owner',
            dataIndex: 'opportunity_owner',
            key: 'opportunity_owner',
            align: 'center',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        ref={this.searchInput}
                        placeholder={`Search Opportunity Owner`}
                        value={selectedKeys[0]}
                        onChange={(e) => {
                            setSelectedKeys(e.target.value ? [e.target.value] : []);
                            this.setInputValue(e.target.value);
                        }}
                        onPressEnter={() => this.handleSearch(selectedKeys as string[], confirm)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => this.handleSearch(selectedKeys as string[], confirm)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                clearFilters && this.handleReset(clearFilters);
                                this.handleSearch(selectedKeys as string[], confirm);
                            }}
                            size="small"
                            style={{ width: 90 }}
                            disabled={!this.state.ownerInputValue}
                        >
                            Reset
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                close();
                            }}
                        >
                            Close
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            ),
            onFilter: (value, record) =>
                record.opportunity_owner ? (
                    record.opportunity_owner.toString().toLowerCase().includes((value as string).toLowerCase())
                ): (
                    false
                ),
            onFilterDropdownOpenChange: (visible) => {
                if (visible) {
                    setTimeout(() => this.searchInput.current?.select(), 100);
                }
            },
            render: (_, record) => (
                record.opportunity_owner ? (
                    <a href={`/user/${record.opportunity_owner}`} rel="noreferrer">
                        {record.opportunity_owner}
                    </a>
                ) : (
                    "-"
                )
            ),
        },
        {
            title: 'Value (ACV)',
            dataIndex: 'acv_value',
            key: 'acv_value',
            align: 'right',
            sorter: (a, b) => a.acv_value - b.acv_value,
            render: (text, record) => (
                <>
                    <Popover
                        placement="top"
                        title={
                            <a href={`${REDMINE_URL}/issues/${record.id}`} target="_blank" rel="noreferrer" className="text-white">
                                {record.subject}
                            </a>
                        }
                        content={
                            <div>
                                <p className="text-white">Setup = ${record.setup.toLocaleString()}</p>
                                <p className="text-white">MRR = ${record.mrr.toLocaleString()}</p>
                            </div>
                        }
                        trigger="hover"
                        color="#3B82F6"
                    >
                        <p className="popover-text">${text.toLocaleString()}</p>
                    </Popover>
                </>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status.name',
            key: 'status',
            filters: [
                {
                    text: 'To do',
                    value: 'To do',
                },
                {
                    text: 'In Progress',
                    value: 'In Progress',
                },
                {
                    text: 'In Review',
                    value: 'In Review',
                },
                {
                    text: 'Done',
                    value: 'Done',
                },
                {
                    text: 'Recurring',
                    value: 'Recurring',
                },
                {
                    text: 'Archived',
                    value: 'Archived',
                },
            ],
            onFilter: (value, record) => record.status.indexOf(value as string) === 0,
            render: (_, record) => (
                record.status ? <p>{record.status}</p> : "-"
            ),
        },
        {
            title: 'Pipeline Stage',
            dataIndex: 'pipeline_stage',
            key: 'pipeline_stage',
            filters: [
                {
                    text: 'Qualification',
                    value: 'Qualification',
                },
                {
                    text: 'Analysis',
                    value: 'Analysis',
                },                
                {
                    text: 'Estimation',
                    value: 'Estimation',
                },
                {
                    text: 'Proposal',
                    value: 'Proposal',
                },
                {
                    text: 'Negotiation',
                    value: 'Negotiation',
                },
                {
                    text: 'Won',
                    value: 'Won',
                },
            ],
            onFilter: (value, record) => record.pipeline_stage.indexOf(value as string) === 0,
            render: (text) => (
                text ? <p>{text}</p> : "-"
            ),
        },
        {
            title: 'Win Probability',
            dataIndex: 'win_probability',
            key: 'win_probability',
            filters: [
                {
                    text: 'High',
                    value: 'High',
                },
                {
                    text: 'Medium',
                    value: 'Medium',
                },
                {
                    text: 'Low',
                    value: 'Low',
                },
            ],
            onFilter: (value, record) => record.win_probability.indexOf(value as string) === 0,
            render: (text) => (
                text ? <p>{text}</p> : "-"
            ),
        },
        {
            title: 'Lead Source',
            dataIndex: 'lead_source',
            key: 'lead_source',
            filters: [
                {
                    text: 'Alliance',
                    value: 'Alliance',
                },                
                {
                    text: 'Content marketing',
                    value: 'Content marketing',
                },
                {
                    text: 'Networking',
                    value: 'Networking',
                },
                {
                    text: 'Organic search',
                    value: 'Organic search',
                },
                {
                    text: 'Paid advertisement',
                    value: 'Paid advertisement',
                },
                {
                    text: 'Prospect outreach',
                    value: 'Prospect outreach',
                },
                {
                    text: 'Referral',
                    value: 'Referral',
                },
                {
                    text: 'Technical interactions',
                    value: 'Technical interactions',
                },
                {
                    text: 'Other',
                    value: 'Other',
                },
            ],
            onFilter: (value, record) => record.lead_source.indexOf(value as string) === 0,            
            render: (text) => (
                text ? <p>{text}</p> : "-"
            ),
        },
        {
            title: 'Create Date',
            dataIndex: 'createDate',
            key: 'createDate',
            sorter: (a, b) => a.start_date.getTime() - b.start_date.getTime(),
            align: 'right',
            render: (_, record) => (
                <p>{fromDateToString(record.start_date)}</p>
            ),
        },
        {
            title: 'Estimated Close Date',
            dataIndex: 'estimated_close_date',
            key: 'estimated_close_date',
            align: 'center',
            sorter: (a, b) => (compareDates(a.estimated_close_date, b.estimated_close_date)),
            render: (text) => (
                text ? <p>{formatDate(new Date(text))}</p> : "-"
            ),
        },
        {
            title: 'Last Update',
            dataIndex: 'last_update_date',
            key: 'last_update_date',
            align: 'center',
            sorter: (a, b) => (compareDates(a.last_update_date, b.last_update_date)),
            render: (text, record) => (
                record.last_update_date ? (
                    <>
                        <Popover
                            placement="topRight"
                            title={
                                <a href={`${REDMINE_URL}/issues/${record.id}`} target="_blank" rel="noreferrer" className="text-white">
                                    {record.subject}
                                </a>
                            }
                            content={
                                <p className="text-white">{parse(record.last_update_text)}</p>
                            }
                            trigger="hover"
                            color="#3B82F6"
                        >
                            <p className="popover-text">{fromDateToString(new Date(text))}</p>
                        </Popover>
                    </>
                ) : (
                    "-"
                )
            )
        },
        {
            title: 'Next Action Date',
            dataIndex: 'next_action_date',
            key: 'next_action_date',
            align: 'center',
            sorter: (a, b) => (compareDates(a.next_action_date, b.next_action_date)),
            render: (text) => (
                text ? <p>{fromDateToString(new Date(text))}</p> : "-"
            ),
        },
    ];

    constructor(props: Props) {
        super(props);
        this.state = {
            salesData: undefined,
            ownerSearchText: '',
            ownerInputValue: '',
        };
    }

    componentDidMount() {
        const handleCurrentResponse = (response: any) => {
            this.setState({
                salesData: SalesDataFromJSON(response.data),
            });
            const currentTime = new Date().getTime();
            localStorage.setItem('lastFetchTime', currentTime.toString());
            localStorage.setItem('salesData', JSON.stringify(response.data));
        };

        const fetchDataAndUpdateState = () => {
            BitmakerAPIClient.get(`/sales`, null, handleCurrentResponse);
        };

        const storedData = localStorage.getItem('salesData');
        if (storedData) {
            this.setState({
                salesData: SalesDataFromJSON(JSON.parse(storedData)),
            });
        }

        const lastFetchTime = localStorage.getItem('lastFetchTime');
        if (lastFetchTime) {
            const currentTime = new Date().getTime();
            const elapsedMinutes = (currentTime - parseInt(lastFetchTime, 10)) / (1000 * 60);

            if (elapsedMinutes > 2) {
                fetchDataAndUpdateState();
            } else {
                const remainingMinutes = 2 - elapsedMinutes;
                setTimeout(fetchDataAndUpdateState, remainingMinutes * 60 * 1000);
            }
        } else {
            fetchDataAndUpdateState();
        }
    }

    public render() {
        return (
            <Layout>
                <Content>
                    <div className="table-container">
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={24} md={6} lg={6}>
                                <div className="table-section">
                                    <div className="table-title">
                                        <Title level={5}>Pipeline Summary</Title>
                                    </div>
                                    <div className="table-scroll">
                                        <Table
                                            columns={this.pipelineSummaryColumns}
                                            dataSource={this.state.salesData?.pipeline_summary}
                                            pagination={false}
                                            size={'small'}
                                            rowClassName={'table-row-dark'}
                                            showHeader={false}
                                            rowKey={record => record.pipeline_summary_name}
                                        />
                                    </div>
                                </div>
                            </Col>
                            <Col xs={24} sm={24} md={18} lg={18}>
                                <div className="table-section">
                                    <div className="table-title">
                                        <Title level={5}>Summary</Title>
                                    </div>
                                    <div className="table-scroll">
                                        <Table
                                            columns={this.summaryColumns}
                                            dataSource={this.state.salesData?.summary}
                                            pagination={false}
                                            rowClassName={'table-row-base'}
                                            rowKey={record => record.summary_name}
                                            scroll={{ x: true }}
                                        />
                                        </div>
                                </div>
                            </Col>
                        </Row>

                        <div className="table-section">
                            <div className="table-title">
                                <Title level={5}>Sales Issues</Title>
                            </div>
                            <div className="table-scroll">
                                <Table
                                    columns={this.issuesColumns}
                                    dataSource={this.state.salesData?.sales_issues}
                                    size={'large'}
                                    pagination={{ pageSize: 10 }}
                                    bordered
                                    rowClassName={(record) =>
                                    record.next_action_date &&
                                    isDateGreaterThan(new Date(), new Date(record.next_action_date))
                                        ? 'table-row-red'
                                        : 'table-row-base'
                                    }
                                    scroll={{ x: true }}
                                    rowKey={record => record.id}
                                />
                            </div>
                        </div>
                    </div>
                </Content>
            </Layout>
        );
    }
}
